import { useTranslation } from 'react-i18next';
import { Heading, Link, Text } from '@bn-apps/design-system-ui-react';
import { Link as RouterLink } from 'react-router-dom';
import routes from '@config/routes';
import tools, { type ToolGroup } from '@config/tools';
import MermaidDiagram from '@components/common/MermaidDiagram';
import styles from './HelpContent.module.scss';

interface HelpContentProps {
    section: string;
}

interface ContentSection {
  content: JSX.Element;
}

type ContentSections = Record<string, ContentSection>;

const HelpContent = ({ section }: HelpContentProps): JSX.Element => {
  const { t } = useTranslation();

  // The help for the Tools page lists the same tools as the page, so the two stay in step
  const toolList = (group: ToolGroup): JSX.Element => (
    <ul>
      {tools.filter((tool) => tool.group === group).map((tool) => (
        <li key={tool.id}>
          <strong>{t(tool.nameKey)}:</strong> {t(tool.descriptionKey)}
        </li>
      ))}
    </ul>
  );

  const content: ContentSections = {
    gettingStarted: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.gettingStarted.intro')}
          </Heading>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.gettingStarted.menusTitle')}</Heading>
          <ul>
            <li>
              <strong>{t('common.data')}:</strong> {t('help.content.gettingStarted.dataDesc')}
            </li>
            <li>
              <strong>{t('tools.title')}:</strong> {t('help.content.gettingStarted.toolsDesc')}
            </li>
            <li>
              <strong>{t('catalog.title')}:</strong> {t('help.content.gettingStarted.catalogDesc')}
            </li>
            <li>
              <strong>{t('docs.title')}:</strong> {t('help.content.gettingStarted.docsDesc')}
            </li>
          </ul>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.gettingStarted.findTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.gettingStarted.findDesc')}</Text>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.gettingStarted.signInTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.gettingStarted.signInDesc')}{' '}
            <Link asChild>
              <RouterLink to={routes.helpAuthentication}>{t('help.content.gettingStarted.signInLink')}</RouterLink>
            </Link>
          </Text>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.gettingStarted.settingsTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.gettingStarted.settingsDesc')}</Text>
        </>
      ),
    },
    authentication: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.authentication.intro')}
          </Heading>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.authentication.accessLevelsTitle')}</Heading>
          <ul>
            <li>
              <strong>{t('help.content.authentication.anonymous')}:</strong> {t('help.content.authentication.anonymousDesc')}
            </li>
            <li>
              <strong>{t('help.content.authentication.authenticated')}:</strong> {t('help.content.authentication.authenticatedDesc')}
            </li>
            <li>
              <strong>{t('help.content.authentication.projectMember')}:</strong> {t('help.content.authentication.projectMemberDesc')}
            </li>
          </ul>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.authentication.howToSignInTitle')}</Heading>
          <ol>
            <li>{t('help.content.authentication.signInStep1')}</li>
            <li>{t('help.content.authentication.signInStep2')}</li>
            <li>{t('help.content.authentication.signInStep3')}</li>
            <li>{t('help.content.authentication.signInStep4')}</li>
          </ol>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.authentication.sessionTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.authentication.sessionDesc')}</Text>
        </>
      ),
    },
    files: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.files.intro')}
          </Heading>
          <Text fontSize='sm'>
            <Link asChild>
              <RouterLink to={routes.files}>{t('help.content.files.pageLink')}</RouterLink>
            </Link>
          </Text>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.files.filtersTitle')}</Heading>
          <ul>
            {['Description', 'Version', 'Created', 'Latest'].map((filter) => (
              <li key={filter}>
                <strong>{t(`help.content.files.filter${filter}`)}:</strong> {t(`help.content.files.filter${filter}Desc`)}
              </li>
            ))}
          </ul>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.files.downloadTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.files.downloadDesc')}</Text>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.files.lineageTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.files.lineageDesc')}{' '}
            <Link asChild>
              <RouterLink to={routes.helpDataLineage}>{t('help.content.files.lineageLink')}</RouterLink>
            </Link>
          </Text>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.files.splitTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.files.splitDesc')}</Text>
        </>
      ),
    },
    datasets: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.datasets.intro')}
          </Heading>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.datasets.datasetsTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.datasets.datasetsDesc')}
          </Text>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.datasets.generationTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.datasets.generationDesc')}
          </Text>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.datasets.apiTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.datasets.apiDesc')}
          </Text>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.datasets.requestTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.datasets.requestDesc')}{' '}
            <Link asChild>
              <RouterLink to={routes.helpSupport}>{t('help.content.datasets.requestLink')}</RouterLink>
            </Link>.
          </Text>
        </>
      ),
    },
    map: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.map.intro')}
          </Heading>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.map.mapControlsTitle')}</Heading>
          <ul>
            <li>{t('help.content.map.control1')}</li>
            <li>{t('help.content.map.control2')}</li>
            <li>{t('help.content.map.control3')}</li>
            <li>{t('help.content.map.control4')}</li>
          </ul>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.map.availableLayersTitle')}</Heading>
          <ul>
            <li>{t('help.content.map.layer1')}</li>
            <li>{t('help.content.map.layer2')}</li>
            <li>{t('help.content.map.layer3')}</li>
          </ul>
        </>
      ),
    },
    tools: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.tools.intro')}
          </Heading>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('search.title')}</Heading>
          <Text fontSize='sm'>{t('help.content.tools.searchDesc')}</Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('tools.allTools')}</Heading>
          <Text fontSize='sm'>{t('help.content.tools.allToolsDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.tools.openTitle')}</Heading>
          {toolList('open')}

          <Heading level={4} fontSize='xs'>{t('tools.sparqlEndpoints')}</Heading>
          <Text fontSize='sm'>{t('help.content.tools.sparqlIntro')}</Text>
          {toolList('sparql')}

          <Heading level={4} fontSize='xs'>{t('tools.confidentialTools')}</Heading>
          <Text fontSize='sm'>{t('help.content.tools.adminIntro')}</Text>
          {toolList('confidential')}

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.tools.accessTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.tools.accessDesc')}{' '}
            <Link asChild>
              <RouterLink to={routes.helpSupport}>{t('help.content.tools.supportLink')}</RouterLink>
            </Link>
          </Text>
        </>
      ),
    },
    reports: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.reports.intro')}
          </Heading>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>
            <Link asChild>
              <RouterLink to={routes.statusReports}>{t('statusReports.title')}</RouterLink>
            </Link>
          </Heading>
          <Text fontSize='sm'>{t('help.content.reports.statusDesc')}</Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>
            <Link asChild>
              <RouterLink to={routes.rinfStatus}>{t('rinfStatus.title')}</RouterLink>
            </Link>
          </Heading>
          <Text fontSize='sm'>{t('help.content.reports.rinfDesc')}</Text>
          <Text fontSize='sm'>{t('help.content.reports.rinfSource')}</Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>
            <Link asChild>
              <RouterLink to={routes.railmlStatus}>{t('railmlStatus.title')}</RouterLink>
            </Link>
          </Heading>
          <Text fontSize='sm'>{t('help.content.reports.railmlDesc')}</Text>
          <Text fontSize='sm'>{t('help.content.reports.railmlSource')}</Text>

          {/* The data quality portal is hidden until it is released
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('tools.validationPortal')}</Heading>
          <Text fontSize='sm'>{t('help.content.reports.portalDesc')}</Text>
          */}
        </>
      ),
    },
    accessibility: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.accessibility.intro')}
          </Heading>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.accessibility.featuresTitle')}</Heading>
          <ul>
            <li>{t('help.content.accessibility.feature1')}</li>
            <li>{t('help.content.accessibility.feature2')}</li>
            <li>{t('help.content.accessibility.feature3')}</li>
            <li>{t('help.content.accessibility.feature4')}</li>
            <li>{t('help.content.accessibility.feature5')}</li>
            <li>{t('help.content.accessibility.feature6')}</li>
          </ul>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.accessibility.shortcutsTitle')}</Heading>
          <ul>
            <li>
              <kbd>Tab</kbd>: {t('help.content.accessibility.shortcut1').replace('Tab: ', '')}
            </li>
            <li>
              <kbd>Enter</kbd> / <kbd>Space</kbd>: {t('help.content.accessibility.shortcut2').replace('Enter or Space: ', '')}
            </li>
            <li>
              {t('help.content.accessibility.shortcut3')}
            </li>
            <li>
              <kbd>Esc</kbd>: {t('help.content.accessibility.shortcut4').replace('Esc: ', '')}
            </li>
          </ul>
        </>
      ),
    },
    dataLineage: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.dataLineage.intro')}
          </Heading>
          <Text fontSize='sm'>
            {t('help.content.dataLineage.description')}
          </Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dataLineage.diagramTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.dataLineage.diagramDescription')}
          </Text>
          <div className={styles.mermaidContainer}>
            <MermaidDiagram
              chart={`graph LR
    BD["BaneData / Maximo"] --> SESAM["Sesam"]
    SESAM -->|railML2.4-nor| TRASE["Trasé"]

    BD["BaneData / Maximo"] --> INNSYN["Innsyn"]
    INNSYN -->|csv, some datasets<br/>unavailable via Sesam| TRASE

    LDB["Linjedatabasen"] --> TRASE

    TRASE --> DIMKG["DIM <br/>Knowledge Graph"]

    INNSYN -->|csv, some datasets<br/>unavailable in Trasé:<br/>joints, balises,<br/>contact wire, ...| DIMKG

    TMS["TMS"] -->|railML 3.2, interlocking info<br/>currently Gjøviksbanen,<br/>needs extension| DIMKG

    FDIM["fDIM"] -->|railML 3.2, interlocking info<br/>different system than DIM,<br/>covers gaps from TMS| DIMKG

    QGIS["DIM QGIS"] -->|csv, some missing<br/>operational points| DIMKG

    TPS["TPS"] -->|csv, stations and<br/>station tracks| DIMKG

    DIMKG -->|graph| RAILML["railML Files"]
    DIMKG --> RINF["RINF export"]
    DIMKG --> DATASETS["Datasets and REST API"]

    classDef source fill:#4a90d9,stroke:#2c5f8a,color:#fff
    classDef intermediate fill:#f5a623,stroke:#c47d10,color:#fff
    classDef kg fill:#7ed321,stroke:#5a9e18,color:#fff
    classDef output fill:#9b59b6,stroke:#7d3c98,color:#fff

    class BD,BD2,BD3,LDB,TMS,FDIM,QGIS,TPS source
    class SESAM,INNSYN,TRASE intermediate
    class DIMKG kg
    class RAILML,RINF,DATASETS output`}
            />
          </div>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dataLineage.sourcesTitle')}</Heading>
          <ul>
            <li>
              <strong>{t('help.content.dataLineage.baneDataMaximo')}:</strong> {t('help.content.dataLineage.baneDataMaximoDesc')}
            </li>
            <li>
              <strong>{t('help.content.dataLineage.linjedatabasen')}:</strong> {t('help.content.dataLineage.linjedatabasenDesc')}
            </li>
            <li>
              <strong>{t('help.content.dataLineage.tms')}:</strong> {t('help.content.dataLineage.tmsDesc')}
            </li>
            <li>
              <strong>{t('help.content.dataLineage.fdim')}:</strong> {t('help.content.dataLineage.fdimDesc')}
            </li>
            <li>
              <strong>{t('help.content.dataLineage.dimQgis')}:</strong> {t('help.content.dataLineage.dimQgisDesc')}
            </li>
            <li>
              <strong>{t('help.content.dataLineage.tps')}:</strong> {t('help.content.dataLineage.tpsDesc')}
            </li>
          </ul>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dataLineage.intermediateTitle')}</Heading>
          <ul>
            <li>
              <strong>{t('help.content.dataLineage.sesam')}:</strong> {t('help.content.dataLineage.sesamDesc')}
            </li>
            <li>
              <strong>{t('help.content.dataLineage.innsyn')}:</strong> {t('help.content.dataLineage.innsynDesc')}
            </li>
            <li>
              <strong>{t('help.content.dataLineage.trase')}:</strong> {t('help.content.dataLineage.traseDesc')}
            </li>
          </ul>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dataLineage.outputTitle')}</Heading>
          <ul>
            <li>
              <strong>{t('help.content.dataLineage.dimKg')}:</strong> {t('help.content.dataLineage.dimKgDesc')}
            </li>
            <li>
              <strong>{t('help.content.dataLineage.railmlFiles')}:</strong> {t('help.content.dataLineage.railmlFilesDesc')}
            </li>
            <li>
              <strong>{t('help.content.dataLineage.rinfExport')}:</strong> {t('help.content.dataLineage.rinfExportDesc')}
            </li>
            <li>
              <strong>{t('help.content.dataLineage.datasets')}:</strong> {t('help.content.dataLineage.datasetsDesc')}
            </li>
          </ul>

        </>
      ),
    },
    dataCatalog: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.dataCatalog.intro')}
          </Heading>
          <Text fontSize='sm'>
            {t('help.content.dataCatalog.description')}
          </Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dataCatalog.isTitle')}</Heading>
          <ul>
            <li>{t('help.content.dataCatalog.isCurrent')}</li>
            <li>{t('help.content.dataCatalog.isCounted')}</li>
            <li>{t('help.content.dataCatalog.isRefreshed')}</li>
          </ul>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dataCatalog.isNotTitle')}</Heading>
          <ul>
            <li>{t('help.content.dataCatalog.isNotSchema')}</li>
            <li>{t('help.content.dataCatalog.isNotAllowed')}</li>
            <li>{t('help.content.dataCatalog.isNotMandatory')}</li>
          </ul>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dataCatalog.exampleTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.dataCatalog.exampleIntro')}
          </Text>
          <table className={styles.comparisonTable}>
            <thead>
              <tr>
                <th>{t('help.content.dataCatalog.examplePropertyCol')}</th>
                <th>{t('help.content.dataCatalog.exampleEntitiesCol')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('help.content.dataCatalog.exampleLength')} <code>railml3:hasLength</code></td>
                <td>{t('help.content.dataCatalog.exampleCount', { value: 760, total: 760, percent: 100 })}</td>
              </tr>
              <tr>
                <td>{t('help.content.dataCatalog.exampleHeight')} <code>bno:hasHeight</code></td>
                <td>{t('help.content.dataCatalog.exampleCount', { value: 744, total: 760, percent: 98 })}</td>
              </tr>
            </tbody>
          </table>
          <Text fontSize='sm'>
            {t('help.content.dataCatalog.exampleQuestionsIntro')}
          </Text>
          <table className={styles.comparisonTable}>
            <thead>
              <tr>
                <th>{t('help.content.dataCatalog.questionCol')}</th>
                <th>{t('help.content.dataCatalog.catalogCol')}</th>
                <th>{t('help.content.dataCatalog.schemaCol')}</th>
              </tr>
            </thead>
            <tbody>
              {['Properties', 'Mandatory', 'Violations'].map(question => (
                <tr key={question}>
                  <td><strong>{t(`help.content.dataCatalog.question${question}`)}</strong></td>
                  <td>{t(`help.content.dataCatalog.catalog${question}`)}</td>
                  <td>{t(`help.content.dataCatalog.schema${question}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Text fontSize='sm'>
            {t('help.content.dataCatalog.shaclIntro')}
          </Text>
          <pre className={styles.codeBlock}><code>{`bno:PlatformEdgeShape
    a sh:NodeShape ;
    sh:targetClass railml3:PlatformEdge ;
    sh:property [
        sh:path bno:hasHeight ;
        sh:class bno:Height ;
        sh:minCount 1 ;
        sh:maxCount 1 ;
    ] .`}</code></pre>
          <Text fontSize='sm'>
            {t('help.content.dataCatalog.shaclOutro')}
          </Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dataCatalog.workTitle')}</Heading>
          <ul>
            <li>
              <strong>{t('help.content.dataCatalog.schemaTitle')}:</strong> {t('help.content.dataCatalog.schemaDesc')}
            </li>
            <li>
              <strong>{t('help.content.dataCatalog.lineageTitle')}:</strong> {t('help.content.dataCatalog.lineageDesc')}{' '}
              <Link asChild>
                <RouterLink to={routes.helpDataLineage}>{t('help.content.dataCatalog.lineageLink')}</RouterLink>
              </Link>
            </li>
          </ul>
        </>
      ),
    },
    tpsMapping: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.tpsMapping.intro')}
          </Heading>
          <Text fontSize='sm'>
            {t('help.content.tpsMapping.description')}{' '}
            <Link asChild>
              <RouterLink to={routes.tpsMapping}>{t('help.content.tpsMapping.pageLink')}</RouterLink>
            </Link>
          </Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.tpsMapping.mapsTitle')}</Heading>
          <ul>
            <li>
              <strong>{t('help.content.tpsMapping.dimMap')}:</strong> {t('help.content.tpsMapping.dimMapDesc')}
            </li>
            <li>
              <strong>{t('help.content.tpsMapping.tpsMap')}:</strong> {t('help.content.tpsMapping.tpsMapDesc')}
            </li>
            <li>
              <strong>{t('help.content.tpsMapping.labels')}:</strong> {t('help.content.tpsMapping.labelsDesc')}
            </li>
          </ul>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.tpsMapping.colorsTitle')}</Heading>
          <ul>
            {['Mapped', 'Unmapped', 'Highlighted', 'Selected', 'Overlap'].map((color) => (
              <li key={color}>
                <strong>{t(`help.content.tpsMapping.color${color}`)}:</strong> {t(`help.content.tpsMapping.color${color}Desc`)}
              </li>
            ))}
          </ul>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.tpsMapping.useTitle')}</Heading>
          <ul>
            {['Hover', 'Click', 'DoubleClick', 'Zoom', 'Osm'].map((action) => (
              <li key={action}>
                <strong>{t(`help.content.tpsMapping.use${action}`)}:</strong> {t(`help.content.tpsMapping.use${action}Desc`)}
              </li>
            ))}
          </ul>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.tpsMapping.statsTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.tpsMapping.statsIntro')}
          </Text>
          <ul>
            {['Infraversion', 'DimMapped', 'TpsMapped', 'Mappings'].map((stat) => (
              <li key={stat}>
                <strong>{t(`help.content.tpsMapping.stat${stat}`)}:</strong> {t(`help.content.tpsMapping.stat${stat}Desc`)}
              </li>
            ))}
          </ul>
          <Text fontSize='sm'>
            {t('help.content.tpsMapping.statsNote')}
          </Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.tpsMapping.fineTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.tpsMapping.fineIntro')}
          </Text>
          <ul>
            {['Rows', 'Axis', 'Nodes', 'Overlaps', 'Order', 'Excluded'].map((part) => (
              <li key={part}>
                <strong>{t(`help.content.tpsMapping.fine${part}`)}:</strong> {t(`help.content.tpsMapping.fine${part}Desc`)}
              </li>
            ))}
          </ul>
          <Text fontSize='sm'>
            {t('help.content.tpsMapping.fineExample')}
          </Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.tpsMapping.sourceTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.tpsMapping.sourceDesc')}
          </Text>
        </>
      ),
    },
    dereferencing: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.dereferencing.intro')}
          </Heading>
          <Text fontSize='sm'>
            {t('help.content.dereferencing.description')}
          </Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dereferencing.whatIsTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.dereferencing.whatIsDesc')}
          </Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dereferencing.howItWorksTitle')}</Heading>
          <ol>
            <li>{t('help.content.dereferencing.step1')}</li>
            <li>{t('help.content.dereferencing.step2')}</li>
            <li>{t('help.content.dereferencing.step3')}</li>
          </ol>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dereferencing.entityPageTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.dereferencing.entityPageIntro')}</Text>
          <ul>
            {['Properties', 'Geometry', 'SeeAlso', 'Rdf', 'AnyUri'].map((part) => (
              <li key={part}>
                <strong>{t(`help.content.dereferencing.entityPage${part}`)}:</strong> {t(`help.content.dereferencing.entityPage${part}Desc`)}
              </li>
            ))}
          </ul>
          <Text fontSize='sm'>{t('help.content.dereferencing.entityPageOpen')}</Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dereferencing.contentNegotiationTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.dereferencing.contentNegotiationDesc')}
          </Text>
          <ul>
            <li>
              <strong>{t('help.content.dereferencing.htmlResponse')}:</strong> {t('help.content.dereferencing.htmlResponseDesc')}
            </li>
            <li>
              <strong>{t('help.content.dereferencing.rdfResponse')}:</strong> {t('help.content.dereferencing.rdfResponseDesc')}
            </li>
          </ul>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dereferencing.namespacesTitle')}</Heading>
          <ul>
            <li>
              <strong>{t('help.content.dereferencing.dataNamespace')}:</strong> {t('help.content.dereferencing.dataNamespaceDesc')}
            </li>
            <li>
              <strong>{t('help.content.dereferencing.ontologyNamespace')}:</strong> {t('help.content.dereferencing.ontologyNamespaceDesc')}
            </li>
          </ul>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.dereferencing.exampleTitle')}</Heading>
          <Text fontSize='sm'>
            {t('help.content.dereferencing.exampleDesc')}
          </Text>
        </>
      ),
    },
    definitions: {
      content: (
        <>
          <Heading level={2} fontSize='md'>
            {t('help.content.definitions.intro')}
          </Heading>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.definitions.abbreviationsTitle')}</Heading>
          <table className={styles.comparisonTable}>
            <thead>
              <tr>
                <th>{t('help.content.definitions.abbreviationsAbbrCol')}</th>
                <th>{t('help.content.definitions.abbreviationsFullCol')}</th>
                <th>{t('help.content.definitions.terminologyNotesCol')}</th>
              </tr>
            </thead>
            <tbody>
              {[
                // Abbreviations that also have a concept description
                ['abbrevDim',   'abbrevDimFull',   'conceptDimDesc'],
                ['abbrevRdf',   'abbrevRdfFull',   'conceptRdfDesc'],
                ['abbrevIri',   'abbrevIriFull',   'conceptIriDesc'],
                ['abbrevSparql','abbrevSparqlFull','conceptSparqlDesc'],
                ['abbrevShacl', 'abbrevShaclFull', 'conceptShaclDesc'],
                ['abbrevRinf',  'abbrevRinfFull',  'abbrevRinfNote'],
                ['abbrevEra',   'abbrevEraFull',   'abbrevEraNote'],
                ['abbrevNre',   'abbrevNreFull',   'abbrevNreNote'],
                ['abbrevRne',   'abbrevRneFull',   'abbrevRneNote'],
                ['abbrevErtms', 'abbrevErtmsFull', 'abbrevErtmsNote'],
                ['abbrevAto',   'abbrevAtoFull',   'abbrevAtoNote'],
                ['abbrevRcc',   'abbrevRccFull',   'abbrevRccNote'],
                ['abbrevIm',    'abbrevImFull',    'abbrevImNote'],
                // Concept-only terms (no abbreviation / no full form)
                ['conceptKnowledgeGraph', null, 'conceptKnowledgeGraphDesc'],
                ['conceptTriple',         null, 'conceptTripleDesc'],
                ['conceptOntology',       null, 'conceptOntologyDesc'],
                ['conceptLinkedData',     null, 'conceptLinkedDataDesc'],
                ['conceptSemanticGraph',  null, 'conceptSemanticGraphDesc'],
                ['conceptMapping',        null, 'conceptMappingDesc'],
              ].map(([termKey, fullKey, notesKey]) => (
                <tr key={termKey}>
                  <td><strong>{t(`help.content.definitions.${termKey}`)}</strong></td>
                  <td>{fullKey ? t(`help.content.definitions.${fullKey}`) : '–'}</td>
                  <td>{notesKey ? t(`help.content.definitions.${notesKey}`) : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.definitions.basicConceptsTitle')}</Heading>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.kgTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.kgDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.rdfTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.rdfDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.tripleTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.tripleDesc')}</Text>
          <ul>
            <li>{t('help.content.definitions.tripleSubject')}</li>
            <li>{t('help.content.definitions.triplePredicate')}</li>
            <li>{t('help.content.definitions.tripleObject')}</li>
          </ul>
          <Text fontSize='sm'>{t('help.content.definitions.tripleNote')}</Text>

          <div className={styles.mermaidContainerSmall}>
            <MermaidDiagram
              chart={`graph LR
    S["🚦 Signal A"]:::subject -->|stands at| O["🛤️ Track 1"]:::object
    classDef subject fill:#4a90d9,stroke:#2c5f8a,color:#fff
    classDef object fill:#7ed321,stroke:#5a9e18,color:#fff`}
            />
            <div className={styles.tripleLabels}>
              <span>{t('help.content.definitions.tripleSubjectLabel')}</span>
              <span>{t('help.content.definitions.triplePredicateLabel')}</span>
              <span>{t('help.content.definitions.tripleObjectLabel')}</span>
            </div>
          </div>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.iriTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.iriDesc')}</Text>
          <ul>
            <li>{t('help.content.definitions.iriAvoidConfusion')}</li>
            <li>{t('help.content.definitions.iriLinkData')}</li>
            <li>{t('help.content.definitions.iriReference')}</li>
          </ul>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.sparqlTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.sparqlDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.shaclTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.shaclDesc')}</Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.definitions.meaningStructureTitle')}</Heading>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.ontologyTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.ontologyDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.dataModelTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.dataModelDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.ontologyVsDataModelTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.ontologyVsDataModelDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.semanticTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.semanticDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.linkedDataTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.linkedDataDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.mappingTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.mappingDesc')}</Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.definitions.differencesTitle')}</Heading>
          <table className={styles.comparisonTable}>
            <thead>
              <tr>
                <th>{t('help.content.definitions.diffTraditionalDb')}</th>
                <th>{t('help.content.definitions.diffKnowledgeGraph')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('help.content.definitions.diffRow1Traditional')}</td>
                <td>{t('help.content.definitions.diffRow1Kg')}</td>
              </tr>
              <tr>
                <td>{t('help.content.definitions.diffRow2Traditional')}</td>
                <td>{t('help.content.definitions.diffRow2Kg')}</td>
              </tr>
              <tr>
                <td>{t('help.content.definitions.diffRow3Traditional')}</td>
                <td>{t('help.content.definitions.diffRow3Kg')}</td>
              </tr>
              <tr>
                <td>{t('help.content.definitions.diffRow4Traditional')}</td>
                <td>{t('help.content.definitions.diffRow4Kg')}</td>
              </tr>
            </tbody>
          </table>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.sparqlVsRestTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.sparqlVsRestDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.openClosedWorldTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.openClosedWorldDesc')}</Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.definitions.whyDimTitle')}</Heading>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.problemTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.problemDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.benefitsTitle')}</Heading>
          <ul>
            <li>{t('help.content.definitions.benefit1')}</li>
            <li>{t('help.content.definitions.benefit2')}</li>
            <li>{t('help.content.definitions.benefit3')}</li>
            <li>{t('help.content.definitions.benefit4')}</li>
            <li>{t('help.content.definitions.benefit5')}</li>
          </ul>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.whyRdfFirstTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.whyRdfFirstDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.beyondRailmlTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.beyondRailmlDesc')}</Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.definitions.europeTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.europeIntro')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.networkStatementTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.networkStatementDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.rinfTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.rinfDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.eraTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.eraDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.nreTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.nreDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.rneTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.rneDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.ertmsTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.ertmsDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.atoTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.atoDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.rccTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.rccDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.europeConnectionTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.europeConnectionDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.dimFitTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.dimFitDesc')}</Text>

          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.definitions.topologyTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.topologyIntro')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.whatIsTopologyTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.whatIsTopologyDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.topologyAndRailwayTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.topologyAndRailwayDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.railTopoModelTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.railTopoModelDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.topologyLevelsTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.topologyLevelsDesc')}</Text>

          <Heading level={5} fontSize='xs'><em>{t('help.content.definitions.microscopicTitle')}</em></Heading>
          <Text fontSize='sm'>{t('help.content.definitions.microscopicDesc')}</Text>

          <Heading level={5} fontSize='xs'><em>{t('help.content.definitions.mesoscopicTitle')}</em></Heading>
          <Text fontSize='sm'>{t('help.content.definitions.mesoscopicDesc')}</Text>

          <Heading level={5} fontSize='xs'><em>{t('help.content.definitions.macroscopicTitle')}</em></Heading>
          <Text fontSize='sm'>{t('help.content.definitions.macroscopicDesc')}</Text>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.topologyUsesTitle')}</Heading>
          <ul>
            <li>{t('help.content.definitions.topologyUse1')}</li>
            <li>{t('help.content.definitions.topologyUse2')}</li>
            <li>{t('help.content.definitions.topologyUse3')}</li>
            <li>{t('help.content.definitions.topologyUse4')}</li>
            <li>{t('help.content.definitions.topologyUse5')}</li>
          </ul>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.topologyImportanceTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.topologyImportanceDesc')}</Text>
          <ul>
            <li>{t('help.content.definitions.topologyImportance1')}</li>
            <li>{t('help.content.definitions.topologyImportance2')}</li>
            <li>{t('help.content.definitions.topologyImportance3')}</li>
            <li>{t('help.content.definitions.topologyImportance4')}</li>
            <li>{t('help.content.definitions.topologyImportance5')}</li>
          </ul>

          <Heading level={4} fontSize='xs'>{t('help.content.definitions.topologyAndDimTitle')}</Heading>
          <Text fontSize='sm'>{t('help.content.definitions.topologyAndDimDesc')}</Text>
        </>
      ),
    },
    support: {
      content: (
        <>
          <Heading level={2} fontSize='md'>{t('help.content.support.intro')}</Heading>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.support.channelsTitle')}</Heading>
          <ul>
            <li>
              <strong>{t('help.content.support.email')}:</strong>{' '}
              <Link href={`mailto:${t('help.content.support.emailDesc')}`}>
                {t('help.content.support.emailDesc')}
              </Link>
            </li>
            <li>
              <strong>{t('help.content.support.azureDevOps')}:</strong>{' '}
              <Link
                href="https://dev.azure.com/bn-apps/Digital%20Infrastrukturmodell%20(DIM)"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('help.content.support.azureDevOpsDesc')}
              </Link>
            </li>
            <li>
              <strong>{t('help.content.support.github')}:</strong>{' '}
              <Link
                href="https://github.com/orgs/bn-apps/projects/8"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('help.content.support.githubDesc')}
              </Link>
            </li>
            <li>
              <strong>{t('help.content.support.documentation')}:</strong> {t('help.content.support.documentationDesc')}
            </li>
          </ul>
          <Heading level={3} fontSize='sm' className={styles.subtitle}>{t('help.content.support.commonIssuesTitle')}</Heading>
          <ul>
            <li>
              <strong>{t('help.content.support.issue1Title')}:</strong> {t('help.content.support.issue1Desc')}
            </li>
            <li>
              <strong>{t('help.content.support.issue2Title')}:</strong> {t('help.content.support.issue2Desc')}
            </li>
            <li>
              <strong>{t('help.content.support.issue3Title')}:</strong> {t('help.content.support.issue3Desc')}{' '}
              <Link asChild>
                <RouterLink to={routes.helpAuthentication}>{t('help.authentication')}</RouterLink>
              </Link>
            </li>
          </ul>
        </>
      ),
    },
  };

  const sectionContent = content[section] || content.gettingStarted;

  return (
    <div className={styles.helpContent}>
      <div>{sectionContent.content}</div>
    </div>
  );
};

export default HelpContent;
